import { Injectable } from '@angular/core';
import {
  CheckinFunctionIdType,
  CheckinPageIdType,
  NEXT_ACTION,
  ParameterFromServicing,
  ParameterToServicing,
  RESERVATION_DETAIL_IDENTIFIER,
  SITE_ID,
} from '../../interface';
import { CheckinCommonState } from '../../store';
import { CheckinCommonStoreService } from '../checkin-common-store/checkin-common-store.service';
import {
  JourneyElement,
  PostJourneysGetJourneyRequest,
  PostJourneysGetJourneyResponse,
  PostJourneysGetJourneyResponseData,
  PostJourneysGetJourneyResponseDataTravelersInner,
  PostJourneysSearchJourneyRequest,
} from 'src/sdk_checkin';
import { AswServiceModel, SessionStorageName } from '@lib/interfaces';
import { AswMasterService, AswServiceStoreService, CommonLibService } from '@lib/services';
import { CheckinBaseClass } from '../../components';
import { LOAD_MASTERS } from '../../master/master';
import { JourneyElements } from '../../interface/journey-elements';
import { PREVIOUS_PAGE_IDENTIFIER } from '@conf/previous-page-identifier.config';
import { ServicingMybookingTransitionService } from '../servicing-mybooking-transition/servicing-mybooking-transition.service';
import {
  GetOrderRequest,
  GetOrderResponse,
  GetOrderResponseDataAir,
  GetOrderResponseDataOrderEligibilitiesChange,
} from 'src/sdk-servicing';
import { Observable } from 'rxjs';
import { ErrorCodeConstants, WarningCodeConstants } from '@conf/app.constants';

@Injectable()
export class CheckinMybookingService extends CheckinBaseClass {
  /** サービシングから受け取るパラメータ */
  private _parameterFromServicing: ParameterFromServicing | undefined;
  /** チェックイン共通から受け取るパラメータ */
  private _parameterFromCheckinCommon: CheckinCommonState | undefined;
  /** サービス共通を取得するためのObservable */
  _aswServiceModel$: Observable<AswServiceModel> = this.aswServiceStoreService.getAswService$();
  /** サービス共通 */
  _aswServiceModel!: AswServiceModel;

  /** APIから返却されたエラーコードをエラーメッセージIDに変換する(チェックイン取り消しAPI) */
  private readonly API_ERROR_CODE_TO_MESSAGE_KEY_CANCEL_CHECKIN: { [errorCode: string]: string } = {
    [ErrorCodeConstants.ERROR_CODES.EBAZ000349]: 'E0597',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000350]: 'E0598',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000351]: 'E0599',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000776]: 'E1782',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000781]: 'E1774',
  };

  /** APIから返却されたエラーコードをエラーメッセージIDに変換する(空席待ち削除API) */
  private readonly API_ERROR_CODE_TO_MESSAGE_KEY_CANCEL_STANDBY: { [errorCode: string]: string } = {
    [ErrorCodeConstants.ERROR_CODES.EBAZ000816]: 'E1755',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000818]: 'E1757',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000819]: 'E1775',
  };

  /** APIから返却されたワーニングコードをワーニングメッセージIDに変換する(チェックイン取り消しAPI) */
  private readonly API_WARNING_CODE_TO_MESSAGE_KEY_CANCEL_CHECKIN: { [warningCode: string]: string } = {
    [WarningCodeConstants.WARNING_CODES.WBAZ000247]: 'W0796',
    [WarningCodeConstants.WARNING_CODES.WBAZ000578]: 'W1312',
    [WarningCodeConstants.WARNING_CODES.WBAZ000579]: 'W1313',
    [WarningCodeConstants.WARNING_CODES.WBAZ000580]: 'W1313',
  };

  constructor(
    private common: CommonLibService,
    private checkinCommonStoreService: CheckinCommonStoreService,
    private masterService: AswMasterService,
    private servicingMybookingTransitionService: ServicingMybookingTransitionService,
    private aswServiceStoreService: AswServiceStoreService
  ) {
    super();
    // サービシングから遷移した際パラメータの取得
    this._parameterFromServicing = this.servicingMybookingTransitionService.getParameterFromServicing();
    // チェックイン系から遷移した際のパラメータの取得
    this._parameterFromCheckinCommon = this.getParameterFromCheckinCommon();

    // サービシングからの遷移かどうかを設定する
    if (this._parameterFromServicing?.SITE_ID === SITE_ID.RESERVATION) {
      // サービシングから受け取ったパラメータをチェックイン系の他の画面でも使用できるようにチェックイン共通に格納
      this.checkinCommonStoreService.updateCheckinCommon({
        journeyId: this._parameterFromServicing.journeyID,
        selectedFlightIds: this._parameterFromServicing.flightIds,
        reservationDetailIdentifier: RESERVATION_DETAIL_IDENTIFIER,
      });
    } else {
      this.checkinCommonStoreService.updateCheckinCommon({
        reservationDetailIdentifier: '',
      });
    }

    // サービス共通のパラメータ取得
    this.subscribeService(this._aswServiceModel$, (data) => {
      this._aswServiceModel = data;
    });
  }

  get parameterFromServicing() {
    return this._parameterFromServicing;
  }

  get parameterFromCheckinCommon() {
    return this._parameterFromCheckinCommon;
  }

  get aswServiceModel() {
    return this._aswServiceModel;
  }

  /**
   * 画面遷移元からパラメータを受け取り、CPR情報取得APIのリクエストパラメータに成形して返却する
   * @returns CPR情報取得APIのリクエストパラメータ
   */
  getPostJourneysGetJourneyRequestParameter(): PostJourneysGetJourneyRequest | undefined {
    // サービシングからのパラメータを取得
    if (this._parameterFromServicing?.SITE_ID) {
      const journeyId = this._parameterFromServicing.journeyID;
      if (journeyId) {
        return {
          journeyId: journeyId,
          searchProperty: {
            isMaskingRequired: true,
            isUnFlightsReturnRequired: true,
            isWaitlistReturnRequired: true,
            isWaitlistDetailsReturnRequired: true,
          },
        };
      }
      // サービシングから受け取るパラメータから有効なパラメータが取得できない場合
      return undefined;
    }

    // サービシングからの遷移でない場合、チェックイン共通からパラメータの取得
    if (this._parameterFromCheckinCommon) {
      const journeyId = this._parameterFromCheckinCommon.journeyId;
      if (journeyId) {
        return {
          journeyId: journeyId,
          searchProperty: {
            isMaskingRequired: true,
            isUnFlightsReturnRequired: true,
            isWaitlistReturnRequired: true,
            isWaitlistDetailsReturnRequired: true,
          },
        };
      }
      // チェックイン共通から有効なパラメータが取得できない場合
      return undefined;
    }

    // どこのパラメータもundefinedの場合
    return undefined;
  }

  /**
   * チェックイン共通から必要なパラメータを取得する
   * @returns チェックイン共通に保存されているパラメータ
   */
  private getParameterFromCheckinCommon(): CheckinCommonState {
    return this.checkinCommonStoreService.checkinCommon;
  }

  /**
   * サービスで保持しているチェックイン共通の値を更新する。
   */
  updateParameterFromCheckinCommon() {
    this._parameterFromCheckinCommon = this.getParameterFromCheckinCommon();
  }

  /**
   * 予約詳細画面へ遷移する
   * @param cprResult CPR取得結果
   */
  transitionServicingMybooking(cprResult: PostJourneysGetJourneyResponse) {
    let param: ParameterToServicing = {};
    if (this._parameterFromServicing && this._parameterFromServicing.SITE_ID === SITE_ID.RESERVATION) {
      // 予約詳細画面からの遷移の場合
      const { orderId, firstName, lastName } = this._parameterFromServicing;

      // 遷移先に渡すパラメータの設定
      param = {
        JSessionId: sessionStorage.getItem(SessionStorageName.JSESSION_ID),
        searchType: 'order',
        orderId,
        firstName,
        lastName,
        SITE_ID: SITE_ID.ASW_APP,
        travelers: this.servicingMybookingTransitionService.concatTravelerNames(cprResult.data.travelers),
      };
    } else if (
      this.checkinCommonStoreService.checkinCommon.previousPageIdentifier ===
      PREVIOUS_PAGE_IDENTIFIER.SERVICING_MY_BOOKING
    ) {
      // 予約詳細画面からのチェックイン検索経由で遷移の場合
      const { orderId, travelDocumentId, firstName, lastName } = this.common.aswServiceStoreService.aswServiceData;

      if (
        this.checkinCommonStoreService.checkinCommon.searchType ===
        PostJourneysSearchJourneyRequest.SearchTypeEnum.Order
      ) {
        param = {
          JSessionId: sessionStorage.getItem(SessionStorageName.JSESSION_ID),
          searchType: 'order',
          orderId,
          firstName,
          lastName,
          SITE_ID: SITE_ID.ASW_APP,
          travelers: this.servicingMybookingTransitionService.concatTravelerNames(cprResult.data.travelers),
        };
      } else {
        param = {
          JSessionId: sessionStorage.getItem(SessionStorageName.JSESSION_ID),
          searchType: 'eTicket',
          eTicketNumber: travelDocumentId,
          firstName,
          lastName,
          SITE_ID: SITE_ID.ASW_APP,
          travelers: this.servicingMybookingTransitionService.concatTravelerNames(cprResult.data.travelers),
        };
      }
    } else {
      // 予約詳細画面以外からの遷移の場合
      const journeyElements = cprResult.data.journeyElements as JourneyElements;
      // 遷移先に渡すパラメータの設定
      param = {
        JSessionId: sessionStorage.getItem(SessionStorageName.JSESSION_ID),
        searchType: 'order',
        orderId: journeyElements[cprResult.data.flights[0].id][cprResult.data.travelers[0].id].orderId,
        firstName: this.common.aswServiceStoreService.aswServiceData.firstName,
        lastName: this.common.aswServiceStoreService.aswServiceData.lastName,
        SITE_ID: SITE_ID.ASW_APP,
        travelers: this.servicingMybookingTransitionService.concatTravelerNames(cprResult.data.travelers),
      };
    }

    this.servicingMybookingTransitionService.transitionToServicing(param);
  }

  /**
   * 戻るボタン押下時に予約詳細に遷移する
   * */
  backToServicingMybooking() {
    let param: ParameterToServicing;

    if (this._parameterFromServicing) {
      // 予約詳細画面からの遷移の場合
      const { orderId, firstName, lastName } = this._parameterFromServicing;

      param = {
        searchType: 'order',
        orderId,
        firstName,
        lastName,
        SITE_ID: SITE_ID.ASW_APP,
      };
    } else {
      // 予約詳細画面からのチェックイン検索経由で遷移の場合
      const { orderId, travelDocumentId, firstName, lastName } = this.common.aswServiceStoreService.aswServiceData;

      if (
        this.checkinCommonStoreService.checkinCommon.searchType ===
        PostJourneysSearchJourneyRequest.SearchTypeEnum.Order
      ) {
        param = {
          searchType: 'order',
          orderId,
          firstName,
          lastName,
          SITE_ID: SITE_ID.ASW_APP,
        };
      } else {
        param = {
          searchType: 'eTicket',
          eTicketNumber: travelDocumentId,
          firstName,
          lastName,
          SITE_ID: SITE_ID.ASW_APP,
        };
      }
    }

    this.servicingMybookingTransitionService.transitionToServicing(param);
  }

  /**
   * 予約詳細（サービス）に遷移する
   * @param travelers 予約詳細画面へ渡す搭乗者情報リストを作成するための搭乗者配列
   * @param flights 予約詳細画面へ渡すorderIdを取得するためのセグメントID
   * @param journeyElements 予約詳細画面へ渡すorderIdを取得するためのjourneyElementオブジェクト
   */
  transitionServicingMybookingService(
    travelers: PostJourneysGetJourneyResponseDataTravelersInner[],
    flightId: string,
    journeyElements: JourneyElements
  ) {
    // どこからの遷移かによって取得元が変更されるパラメータ
    let orderId: string | undefined;
    let firstName: string | undefined;
    let lastName: string | undefined;
    let journeyId: string | undefined;
    let flightIdList: string[] | undefined;

    // 予約詳細から受け取ったパラメータが存在する場合
    if (this._parameterFromServicing) {
      journeyId = this._parameterFromServicing?.journeyID;
      flightIdList = this._parameterFromServicing?.flightIds;
      orderId = this._parameterFromServicing?.orderId;
      firstName = this._parameterFromServicing?.firstName;
      lastName = this._parameterFromServicing?.lastName;
    }
    // チェックインフローからの遷移の場合
    else {
      journeyId = this._parameterFromCheckinCommon?.journeyId;
      flightIdList = this._parameterFromCheckinCommon?.selectedFlightIds;
      const travelerId = travelers[0].id;
      orderId = journeyElements[flightId][travelerId].orderId;
      firstName = this.common.aswServiceStoreService.aswServiceData.firstName;
      lastName = this.common.aswServiceStoreService.aswServiceData.lastName;
    }

    // 遷移先に渡すパラメータの設定
    const param: ParameterToServicing = {
      searchType: 'order',
      orderId: orderId,
      firstName: firstName,
      lastName: lastName,
      nextAction: NEXT_ACTION.SERVICE,
      SITE_ID: SITE_ID.ASW_APP,
      JSessionId: sessionStorage.getItem(SessionStorageName.JSESSION_ID),
      JourneyId: journeyId,
      flightIdList: flightIdList,
      travelers: this.servicingMybookingTransitionService.concatTravelerNames(travelers),
      previousScreenId: `${CheckinFunctionIdType.BOARDING_PROCEDURES}-${CheckinPageIdType.CHECKIN_MYBOOKING}`,
    };

    this.servicingMybookingTransitionService.transitionToServicing(param);
  }

  /**
   * エラーコードを受け取り、エラーメッセージIDを返却する(チェックイン取り消しAPI)
   * @param apiErrorCode
   * @returns 指定されたエラーコードに紐づくエラーメッセージID、存在しない場合はundefined
   */
  getCancelCheckinErrorMessageKey(apiErrorCode: string): string | undefined {
    return this.API_ERROR_CODE_TO_MESSAGE_KEY_CANCEL_CHECKIN[apiErrorCode];
  }

  /**
   * エラーコードを受け取り、エラーメッセージIDを返却する(空席待ち削除API)
   * @param apiErrorCode
   * @returns 指定されたエラーコードに紐づくエラーメッセージID、存在しない場合はundefined
   */
  getCancelStandbyErrorMessageKey(apiErrorCode: string): string | undefined {
    return this.API_ERROR_CODE_TO_MESSAGE_KEY_CANCEL_STANDBY[apiErrorCode];
  }

  /**
   * ワーニングコードを受け取り、ワーニングメッセージIDを返却する(チェックイン取り消しAPI)
   * @param apiWarningCode
   * @returns 指定されたワーニングコードに紐づくワーニングメッセージID、存在しない場合はundefined
   */
  getCancelCheckinWarningMessageKey(apiWarningCode: string): string | undefined {
    return this.API_WARNING_CODE_TO_MESSAGE_KEY_CANCEL_CHECKIN[apiWarningCode];
  }

  /** チェックイン共通Storeにチェックイン状況詳細からの遷移であることを格納する */
  setPreviousIdentifier() {
    this.checkinCommonStoreService.updateCheckinCommon({
      previousPageIdentifier: PREVIOUS_PAGE_IDENTIFIER.CHECKIN_STATUS,
    });
  }
  /**
   * チェックイン状況詳細画面で使用するマスターをロードするObservableを取得する
   * 既にロードされているマスターを上書きしないために、更新フラグをtrueで呼び出す
   */
  getLoadMasters$() {
    return this.masterService.load(
      [
        LOAD_MASTERS.AIRLINE_I18NJOIN_BY_AIRLINE_CODE,
        LOAD_MASTERS.AIRPORT_I18NJOIN_BY_AIRPORT_CODE,
        LOAD_MASTERS.EQUIPMENT_I18N_BY_ACV,
        LOAD_MASTERS.COUNTRY_I18NJOIN_BY_COUNTRY_2_LETTER_CODE,
        LOAD_MASTERS.MILEAGE_PROGRAM_BY_CARRIER_CODE,
        LOAD_MASTERS.STATE_BY_STATE_CODE,
        LOAD_MASTERS.V_LIST_DATA_CABIN_CLASS_BY_SEARCH_KEY,
        LOAD_MASTERS.FUNCTION_INELIGIBLE_REASON,
        LOAD_MASTERS.GENDER_BY_GENDER_FOR_CHECKIN,
        LOAD_MASTERS.POS_COUNTRY_ALL,
        LOAD_MASTERS.COUNTRY_COUNTRYI18N_ALL,
      ],
      true
    );
  }

  /**
   * 画面遷移元からパラメータを受け取り、PNR情報取得APIのリクエストパラメータに成形して返却する
   * @param cprResult CPR取得結果
   * @returns PNR情報取得APIのリクエストパラメータ
   */
  createGetOrderRequestParameter(cprResult: PostJourneysGetJourneyResponse): GetOrderRequest | undefined {
    // サービシングからのパラメータを取得
    if (this._parameterFromServicing?.SITE_ID) {
      const param: GetOrderRequest = {
        credential: {
          firstName: this._parameterFromServicing.firstName ? this._parameterFromServicing.firstName : '',
          lastName: this._parameterFromServicing.lastName ? this._parameterFromServicing.lastName : '',
        },
        mask: false,
        getServiceCatalogue: false,
        // 提携：OBS PNR取得フラグをtrueに設定
        getOperationalBlockSpaceReservationInformation: true,
        // 提携：Bizチェックスキップフラグをtrueに設定
        skipBizCheck: true,
      };

      if (this._parameterFromServicing.eTicketNumber) {
        // 予約詳細からの連携されたパラメータに航空券番号が存在すれば、航空券番号をパラメータとして設定する
        param.travelDocumentId = this._parameterFromServicing.eTicketNumber;
      } else {
        // 予約詳細からの連携されたパラメータに航空券番号が存在しなければ、予約番号をパラメータとして設定する
        param.orderId = this._parameterFromServicing.orderId;
      }

      return param;
    }

    // サービシングからの遷移でない場合、サービス共通からパラメータの取得
    if (this._aswServiceModel && this._aswServiceModel.travelDocumentId) {
      return {
        travelDocumentId: this._aswServiceModel.travelDocumentId,
        credential: {
          firstName: this._aswServiceModel.firstName ? this._aswServiceModel.firstName : '',
          lastName: this._aswServiceModel.lastName ? this._aswServiceModel.lastName : '',
        },
        mask: false,
        getServiceCatalogue: false,
        // 提携：OBS PNR取得フラグをtrueに設定
        getOperationalBlockSpaceReservationInformation: true,
        // 提携：Bizチェックスキップフラグをtrueに設定
        skipBizCheck: true,
      };
    } else if (this._aswServiceModel && this._aswServiceModel.orderId) {
      return {
        orderId: this._aswServiceModel.orderId,
        credential: {
          firstName: this._aswServiceModel.firstName ? this._aswServiceModel.firstName : '',
          lastName: this._aswServiceModel.lastName ? this._aswServiceModel.lastName : '',
        },
        mask: false,
        getServiceCatalogue: false,
        // 提携：OBS PNR取得フラグをtrueに設定
        getOperationalBlockSpaceReservationInformation: true,
        // 提携：Bizチェックスキップフラグをtrueに設定
        skipBizCheck: true,
      };
    } else {
      const journeyElements = cprResult.data.journeyElements as JourneyElements;
      const tragetTraveler = cprResult.data.travelers.find(
        (traveler) =>
          traveler.name?.firstName === this._aswServiceModel.firstName &&
          traveler.name?.lastName === this._aswServiceModel.lastName
      );
      const orderId = journeyElements[cprResult.data.flights[0].id][tragetTraveler?.id!].orderId;
      if (orderId) {
        return {
          orderId: orderId,
          credential: {
            firstName: this._aswServiceModel.firstName ? this._aswServiceModel.firstName : '',
            lastName: this._aswServiceModel.lastName ? this._aswServiceModel.lastName : '',
          },
          mask: false,
          getServiceCatalogue: false,
          // 提携：OBS PNR取得フラグをtrueに設定
          getOperationalBlockSpaceReservationInformation: true,
          // 提携：Bizチェックスキップフラグをtrueに設定
          skipBizCheck: true,
        };
      }
    }
    // どこのパラメータもundefinedの場合
    return undefined;
  }

  /** 空席待ちボタン表示対象セグメントIDを取得する
   * @param cprResult CPR取得結果
   * @returns 空席待ちボタン表示対象セグメントID
   */
  getStandbyButtonDisplaySegmentId(cprResult: PostJourneysGetJourneyResponse) {
    let targetSegmentId = '';
    const journeyElements = cprResult.data.journeyElements as JourneyElements;

    for (const flight of cprResult.data.flights) {
      if (
        !flight.isWaitlistedStatus &&
        !flight.isSpaceAvailableStatus &&
        cprResult.data.travelers.every(
          (traveler) =>
            journeyElements[flight.id][traveler.id].boardingStatus === JourneyElement.BoardingStatusEnum.NotBoarded
        )
      ) {
        targetSegmentId = flight.id;
        break;
      }
    }
    return targetSegmentId;
  }

  /** チェックイン検索APIリクエストパラメータ作成 */
  createCheckinSearchRequestParameter(): PostJourneysSearchJourneyRequest {
    return {
      searchType: this._parameterFromCheckinCommon?.searchType!,
      orderId: this._aswServiceModel.orderId,
      eTicketNumber: this._aswServiceModel.travelDocumentId,
      firstName: this._aswServiceModel.firstName ? this._aswServiceModel.firstName : '',
      lastName: this._aswServiceModel.lastName ? this._aswServiceModel.lastName : '',
    };
  }

  /** 空席待ち可能かどうか相関チェック
   *  @param cprResult CPR取得結果
   *  @param getOrderResult PNR取得結果
   *  @returns エラーが発生した場合はエラーメッセージID、それ以外はundefinedを返却
   */
  correlationCheckInStandby(
    cprResult: PostJourneysGetJourneyResponse,
    getOrderResult: GetOrderResponse
  ): string | undefined {
    if (getOrderResult.data?.air?.tripType === GetOrderResponseDataAir.TripTypeEnum.International) {
      return 'E1873';
    }

    const journeyElements = cprResult.data.journeyElements as JourneyElements;
    if (
      cprResult.data.travelers.some(
        (traveler) =>
          journeyElements[cprResult.data.flights[0].id][traveler.id].checkInStatus ===
          JourneyElement.CheckInStatusEnum.NotAccepted
      )
    ) {
      return 'E1749';
    }

    const canNotStandby =
      cprResult.data.standbyEntryIneligibleType ===
        PostJourneysGetJourneyResponseData.StandbyEntryIneligibleTypeEnum.Unhandleable ||
      getOrderResult.data?.air?.bounds?.some((bound) => bound.isOtherMethodsConnection) ||
      getOrderResult.data?.orderType?.isAwardBooking ||
      getOrderResult.data?.orderEligibilities?.change?.nonEligibilityReasons?.includes(
        GetOrderResponseDataOrderEligibilitiesChange.NonEligibilityReasonsEnum.UnflownOtherMerchantNdc
      );
    if (canNotStandby) {
      return 'E1752';
    }

    if (
      cprResult.data.standbyEntryIneligibleType ===
      PostJourneysGetJourneyResponseData.StandbyEntryIneligibleTypeEnum.HandleableInAirport
    ) {
      return 'E1751';
    }
    if (
      cprResult.data.standbyEntryIneligibleType ===
      PostJourneysGetJourneyResponseData.StandbyEntryIneligibleTypeEnum.HandleableByUserForUpgradeStandby
    ) {
      return 'E1765';
    }
    if (
      cprResult.data.standbyEntryIneligibleType ===
      PostJourneysGetJourneyResponseData.StandbyEntryIneligibleTypeEnum.HandleableByUserForTransferOption
    ) {
      return 'E1750';
    }

    return undefined;
  }

  /**
   * 戻るボタン押下時に予約詳細画面を経由してアップグレード対象フライト選択画面に遷移する
   * @param cprResult CPR取得結果
   */
  backToUpgradeFlightSelect(cprResult: PostJourneysGetJourneyResponse) {
    const journeyElements = cprResult.data.journeyElements as JourneyElements;
    // 遷移先に渡すパラメータの設定
    const param: ParameterToServicing = {
      searchType: 'order',
      orderId: journeyElements[cprResult.data.flights[0].id][cprResult.data.travelers[0].id].orderId,
      firstName: this._aswServiceModel.firstName,
      lastName: this._aswServiceModel.lastName,
      SITE_ID: SITE_ID.ASW_APP,
      nextAction: NEXT_ACTION.UPGRADE,
    };

    this.servicingMybookingTransitionService.transitionToServicing(param);
  }

  override destroy(): void {}
}
